import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiFilePlus, FiUsers, FiBarChart, FiLogOut, FiSettings, FiChevronDown, FiChevronRight, FiBriefcase, FiUser, FiFileText, FiCreditCard, FiDollarSign, FiBarChart2, FiPercent, FiCalendar, FiFile, FiGitBranch, FiMenu, FiX } from 'react-icons/fi';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

const layoutStyles = `
  .dashboard-shell { display: flex; min-height: 100vh; background: #f3f4f6; }
  .dashboard-sidebar {
    width: 250px; flex-shrink: 0; background: #111827; color: white;
    display: flex; flex-direction: column; padding: 1.5rem 1rem;
  }
  .dashboard-sidebar .brand { font-size: 1.25rem; font-weight: 700; margin-bottom: 2rem; padding: 0 0.5rem; }
  .dashboard-sidebar .org-name { font-size: 0.8rem; color: #9ca3af; padding: 0 0.5rem; margin-bottom: 1.5rem; }
  .dashboard-nav { display: flex; flex-direction: column; gap: 0.25rem; flex: 1; overflow-y: auto; }
  .dashboard-nav a {
    display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem;
    border-radius: 6px; color: #d1d5db; text-decoration: none; font-size: 0.9rem; font-weight: 500;
    transition: all 0.15s ease;
  }
  .dashboard-nav a:hover { background: #1f2937; color: white; }
  .dashboard-nav a.active { background: #3b82f6; color: white; }
  .nav-group-header {
    display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
    padding: 0.75rem; border-radius: 6px; color: #9ca3af; background: none; border: none;
    cursor: pointer; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.05em; width: 100%; text-align: left; margin-top: 0.75rem;
  }
  .nav-group-header:hover { color: white; }
  .nav-group-items { display: flex; flex-direction: column; gap: 0.25rem; padding-left: 0.25rem; }
  .dashboard-logout {
    display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem;
    border-radius: 6px; color: #d1d5db; background: none; border: none; cursor: pointer;
    font-size: 0.9rem; font-weight: 500; width: 100%; text-align: left;
  }
  .dashboard-logout:hover { background: #1f2937; color: white; }
  .dashboard-content { flex: 1; padding: 2rem; overflow-x: hidden; min-width: 0; }

  /* ==================== MOBILE TOP BAR — hidden on desktop ==================== */
  .dashboard-mobile-bar {
    display: none;
  }

  .dashboard-sidebar-overlay {
    display: none;
  }

  /* ==================== MOBILE LAYOUT ==================== */
  @media (max-width: 900px) {
    .dashboard-shell {
      flex-direction: column;
    }

    .dashboard-mobile-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #111827;
      color: white;
      padding: 1rem 1.25rem;
      position: sticky;
      top: 0;
      z-index: 40;
    }

    .dashboard-mobile-bar .brand {
      font-size: 1.05rem;
      font-weight: 700;
    }

    .dashboard-mobile-menu-btn {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0.4rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dashboard-sidebar {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      z-index: 60;
      width: 280px;
      max-width: 82vw;
      transform: translateX(-100%);
      transition: transform 0.25s ease;
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.25);
    }

    .dashboard-sidebar.is-open {
      transform: translateX(0);
    }

    .dashboard-sidebar .brand,
    .dashboard-sidebar .org-name {
      display: none;
    }

    .dashboard-sidebar-close {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }

    .dashboard-sidebar-close button {
      background: none;
      border: none;
      color: #9ca3af;
      cursor: pointer;
      padding: 0.4rem;
      display: flex;
    }

    .dashboard-sidebar-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 50;
    }

    .dashboard-content {
      padding: 1.25rem;
    }
  }

  @media (min-width: 901px) {
    .dashboard-sidebar-close {
      display: none;
    }
  }
`;

const NAV_GROUPS = [
  {
    label: 'People',
    icon: FiUser,
    items: [
      { to: '/dashboard/employees', label: 'Employees' },
      { to: '/dashboard/leave', label: 'Leave' },
      { to: '/dashboard/recruitment', label: 'Recruitment' },
      { to: '/dashboard/onboarding-templates', label: 'Onboarding Templates' },
    ],
  },
  {
    label: 'Payroll',
    icon: FiBriefcase,
    items: [
      { to: '/dashboard/payroll/new', label: 'New Payroll Run' },
    ],
  },
  {
    label: 'Invoicing',
    icon: FiFileText,
    items: [
      { to: '/dashboard/invoicing', label: 'Customers & Invoices' },
    ],
  },
  {
    label: 'AP',
    icon: FiCreditCard,
    items: [
      { to: '/dashboard/ap', label: 'Suppliers & Bills' },
    ],
  },
  {
    label: 'Banking',
    icon: FiDollarSign,
    items: [
      { to: '/dashboard/banking', label: 'Accounts & Transactions' },
    ],
  },
  {
    label: 'Accounting',
    icon: FiBarChart2,
    items: [
      { to: '/dashboard/accounting', label: 'Ledger & Journals' },
    ],
  },
  {
    label: 'VAT',
    icon: FiPercent,
    items: [
      { to: '/dashboard/vat', label: 'Returns & Payments' },
    ],
  },
  {
    label: 'Appointments',
    icon: FiCalendar,
    items: [
      { to: '/dashboard/appointments', label: 'Bookings' },
    ],
  },
  {
    label: 'Workflow',
    icon: FiGitBranch,
    items: [
      { to: '/dashboard/workflow', label: 'Approvals & Comments' },
    ],
  },
  {
    label: 'Documents',
    icon: FiFile,
    items: [
      { to: '/dashboard/documents', label: 'Files & Signatures' },
    ],
  },
  {
    label: 'Reports',
    icon: FiBarChart,
    items: [
      { to: '/dashboard/reports', label: 'Financial & Operational' },
    ],
  },
  { label: 'Departments & Job Titles', items: [{ to: '/dashboard/departments', label: 'Departments & Job Titles' }] },
  { label: 'Payroll Settings', items: [{ to: '/dashboard/payroll-settings', label: 'Schemes & HMRC' }] },
  { label: 'Marketing', items: [{ to: '/dashboard/marketing', label: 'Campaigns & Subscribers' }] },
  { label: 'Notifications', items: [{ to: '/dashboard/notifications', label: 'My Notifications' }] },
  { label: 'Integration Logs', items: [{ to: '/dashboard/integration-logs', label: 'Requests & Sync' }] },
  {
    label: 'Admin',
    icon: FiSettings,
    items: [
      { to: '/dashboard/admin/organisation', label: 'Organisation Profile' },
      { to: '/dashboard/users', label: 'Users' },
      { to: '/dashboard/admin/email-sms', label: 'Email & SMS' },
      { to: '/dashboard/admin/billing', label: 'Billing' },
      { to: '/dashboard/admin/audit-log', label: 'Audit Log' },
      { to: '/dashboard/admin/compliance', label: 'Compliance' },
    ],
  },
];

const DashboardLayout = () => {
  const { user, organisations, logout } = useAuth();
  const { success } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(NAV_GROUPS.map((g) => [g.label, g.items.some((i) => location.pathname.startsWith(i.to))]))
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const orgName = organisations?.[0]?.legalName || 'Your Company';

  // Close the mobile drawer automatically whenever the route changes, so tapping
  // a nav link doesn't leave the drawer open over the new page.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    success('✅ Logged out successfully!');
    navigate('/signin', { replace: true });
  };

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="dashboard-shell">
      <style>{layoutStyles}</style>

      {/* Mobile-only top bar with hamburger toggle — hidden entirely above 900px */}
      <div className="dashboard-mobile-bar">
        <span className="brand">Effective Global Staff</span>
        <button className="dashboard-mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu">
          <FiMenu size={22} />
        </button>
      </div>

      {/* Dark overlay behind the drawer — tapping it closes the menu, same as the close button */}
      {mobileNavOpen && (
        <div className="dashboard-sidebar-overlay" onClick={() => setMobileNavOpen(false)} />
      )}

      <aside className={`dashboard-sidebar ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="dashboard-sidebar-close">
          <button onClick={() => setMobileNavOpen(false)} aria-label="Close menu">
            <FiX size={22} />
          </button>
        </div>
        <div className="brand">Effective Global Staff</div>
        <div className="org-name">{orgName}</div>
        <nav className="dashboard-nav">
          <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''}>
            <FiHome size={18} />
            Overview
          </Link>

          {NAV_GROUPS.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = openGroups[group.label];
            return (
              <div key={group.label}>
                <button className="nav-group-header" onClick={() => toggleGroup(group.label)}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    {GroupIcon && <GroupIcon size={14} />}
                    {group.label}
                  </span>
                  {isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                </button>
                {isOpen && (
                  <div className="nav-group-items">
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={location.pathname.startsWith(item.to) ? 'active' : ''}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <button className="dashboard-logout" onClick={handleLogout}>
          <FiLogOut size={18} />
          Logout ({user?.firstName})
        </button>
      </aside>
      <main className="dashboard-content">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;